// Minimal, dependency-free Markdown → HTML for the agent's streamed answers.
// Output is sanitized by SafeHtml before it ever hits the DOM, so this only
// needs to produce the small tag set SafeHtml allows (p, strong, em, code, pre,
// ul/ol/li, h4/h5, a, table…). Good enough for headings, bold/italic, lists,
// inline code, fenced code, links, and pipe tables during streaming.
const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const inline = (t: string): string => {
  let s = esc(t);
  s = s.replace(/`([^`]+)`/g, (_m, c) => `<code>${c}</code>`);
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  s = s.replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g, '<a href="$2">$1</a>');
  return s;
};

export const markdownToHtml = (md: string): string => {
  if (!md) return '';

  // Pull fenced code blocks out first so their contents aren't parsed.
  const codeBlocks: string[] = [];
  md = md.replace(/```[\w-]*\n?([\s\S]*?)```/g, (_m, code) => {
    codeBlocks.push(`<pre><code>${esc(code.replace(/\n$/, ''))}</code></pre>`);
    return ` CB${codeBlocks.length - 1} `;
  });

  const lines = md.split(/\r?\n/);
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (/^\s*$/.test(line)) { i++; continue; }

    // Restore a code-block placeholder that sits on its own line.
    const cb = line.match(/^ CB(\d+) $/);
    if (cb) { out.push(codeBlocks[+cb[1]]); i++; continue; }

    // Pipe table: a header row followed by a |---|---| separator.
    if (/\|/.test(line) && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && /-/.test(lines[i + 1])) {
      const parseRow = (l: string) => l.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim());
      const headers = parseRow(line);
      i += 2;
      const body: string[][] = [];
      while (i < lines.length && /\|/.test(lines[i]) && !/^\s*$/.test(lines[i])) { body.push(parseRow(lines[i])); i++; }
      out.push(
        '<table><thead><tr>' + headers.map((h) => `<th>${inline(h)}</th>`).join('') + '</tr></thead><tbody>' +
        body.map((r) => '<tr>' + r.map((c) => `<td>${inline(c)}</td>`).join('') + '</tr>').join('') +
        '</tbody></table>',
      );
      continue;
    }

    // Headings (# .. ######) mapped onto the allowed h4/h5 (deeper → bold para).
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const lvl = h[1].length;
      if (lvl <= 2) out.push(`<h4>${inline(h[2])}</h4>`);
      else if (lvl === 3) out.push(`<h5>${inline(h[2])}</h5>`);
      else out.push(`<p><strong>${inline(h[2])}</strong></p>`);
      i++; continue;
    }

    // Unordered list.
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) { items.push(`<li>${inline(lines[i].replace(/^\s*[-*]\s+/, ''))}</li>`); i++; }
      out.push(`<ul>${items.join('')}</ul>`); continue;
    }

    // Ordered list.
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(`<li>${inline(lines[i].replace(/^\s*\d+\.\s+/, ''))}</li>`); i++; }
      out.push(`<ol>${items.join('')}</ol>`); continue;
    }

    // Paragraph: gather until a blank line or a block starter.
    const para: string[] = [];
    while (
      i < lines.length && !/^\s*$/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) && !/^\s*\d+\.\s+/.test(lines[i]) && !/^#{1,6}\s+/.test(lines[i])
    ) { para.push(lines[i]); i++; }
    out.push(`<p>${inline(para.join(' '))}</p>`);
  }

  return out.join('').replace(/ CB(\d+) /g, (_m, n) => codeBlocks[+n]);
};
